// Vike-react's getPageElement() creates the Page element with no props
// (React.createElement(Page, null)). The codebase Pages were written expecting
// destructured pageProps. This Wrapper clones the Page child element with
// `pageContext.pageProps` injected — restoring the props-on-Page convention.
//
// It also installs the TanStack Query provider for every page. The renderer
// extends `vike-react` (see `pages/+config.ts`), which means vike-react owns
// the onRenderHtml/onRenderClient pipeline and the custom files under
// `renderer/+onRender*.tsx` are not executed. Wrapping the provider here
// guarantees `useQuery` always finds a client during SSR and hydration.
//
// See: node_modules/vike-react/dist/integration/getPageElement.js

import type { TemplateId } from "@hantavirus/site-config";
import { HydrationBoundary, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  type ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { usePageContext } from "vike-react/usePageContext";

// Side-effect imports for global + theme CSS. Vike-react walks the SSR module
// graph from this Wrapper (referenced via +config.ts `Wrapper`), so CSS
// imports here are automatically injected as <link rel="stylesheet"> tags by
// vite during SSR rendering. The per-site themes are gated by the inactive
// stubs in each clone's apps/web/src/styles/themes/.
import "#/styles/index.css";
import "#/styles/themes/trackhantavirus.css";
import "#/styles/themes/hantavirusrealtime.css";
import "#/styles/themes/hantavir-us.css";
import "#/styles/templates/newsroom.css?usedTemplate=newsroom";
import "#/styles/templates/bento-console.css?usedTemplate=bento-console";
import "#/styles/templates/editorial-longform.css?usedTemplate=editorial-longform";
import { LocaleReconciler } from "#/components/i18n/LocaleReconciler.js";
import { OfflineBanner } from "#/components/dashboard/offline/OfflineBanner.js";
import { shouldShowPublicChrome, stripLocalePrefix } from "#/components/layout/PublicChrome.js";
import { PublicChromeRouter } from "#/components/layout/PublicChromeRouter.js";
import { StickyMobileCta } from "#/components/layout/StickyMobileCta.js";
import { PWAInstallPrompt } from "#/components/pwa/PWAInstallPrompt.js";
import { PWAUpdateBanner } from "#/components/pwa/PWAUpdateBanner.js";
import {
  SuperadminChrome,
  shouldShowSuperadminChrome,
} from "#/components/superadmin/SuperadminChrome.js";
import { useHasMounted } from "#/hooks/useHasMounted.js";
import { useOfflineCachedOutbreak } from "#/hooks/useOfflineCachedOutbreak.js";
import { createT } from "#/lib/i18n.js";
import { DEFAULT_LOCALE, isLocaleCode } from "#/lib/i18n/locales.js";
import { resolveTemplateIdForRender } from "#/lib/templates/index.js";

let browserQueryClient: QueryClient | undefined;
const AppShellContext = createContext(false);
const SESSION_ID_KEY = "hv:analytics:session";
const VISITOR_ID_KEY = "hv:analytics:visitor";
const HISTORY_PATCH_KEY = "__hvAnalyticsHistoryPatched";
const PUBLIC_MOBILE_CRITICAL_CSS = `
@media (max-width: 768px) {
  html,
  body {
    max-width: 100%;
    overflow-x: hidden;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  img,
  svg,
  video,
  canvas {
    max-width: 100%;
  }

  a[href],
  button,
  [role="button"],
  [role="link"] {
    display: inline-flex;
    min-width: 44px;
    min-height: 44px;
    align-items: center;
  }

  .skip-link {
    position: absolute;
    top: -100px;
    left: 1rem;
    z-index: 100;
    padding: 0.75rem 1rem;
  }

  [role="marquee"] {
    max-width: 100%;
    overflow: hidden;
  }

  .sticky-mobile-cta {
    position: fixed;
    right: auto;
    bottom: 0;
    left: 0;
    z-index: 40;
    width: 100%;
    max-width: 100vw;
    padding: 0.75rem 1rem;
  }

  .sticky-mobile-cta > div {
    display: flex;
    gap: 0.5rem;
  }
}
`;

function getQueryClient(): QueryClient {
  // Server: a fresh client per request — never share across requests.
  if (typeof window === "undefined") {
    return new QueryClient({
      defaultOptions: {
        queries: { staleTime: 30_000, retry: 0 },
      },
    });
  }
  // Browser: singleton so the cache survives client-side navigations.
  if (!browserQueryClient) {
    browserQueryClient = new QueryClient({
      defaultOptions: {
        queries: { staleTime: 30_000, refetchOnWindowFocus: false },
      },
    });
  }
  return browserQueryClient;
}

function createAnalyticsId(): string {
  return typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function getOrCreateStoredAnalyticsId(
  storageName: "localStorage" | "sessionStorage",
  key: string,
  prefix = "",
): string | null {
  if (typeof window === "undefined") return null;
  try {
    const storage = window[storageName];
    const existing = storage.getItem(key);
    if (existing) return existing;
    const id = `${prefix}${createAnalyticsId()}`;
    storage.setItem(key, id);
    return id;
  } catch {
    return null;
  }
}

function getOrCreateAnalyticsSessionId(): string | null {
  return getOrCreateStoredAnalyticsId("sessionStorage", SESSION_ID_KEY);
}

function hasProps(value: unknown): value is Record<string, unknown> {
  return Boolean(
    value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length > 0,
  );
}

function resolveSerializedTemplateId(value: unknown): TemplateId {
  const resolved = resolveTemplateIdForRender(value);
  if (resolved !== "newsroom") return resolved;
  if (import.meta.env.VITE_SITE_ID === "trackhantavirus") return "bento-console";
  return resolved;
}

function getOrCreateAnalyticsVisitorId(): string | null {
  return getOrCreateStoredAnalyticsId("localStorage", VISITOR_ID_KEY, "anon_");
}

function shouldTrackPublicPath(pathname: string): boolean {
  return !(
    pathname.startsWith("/admin") ||
    pathname.startsWith("/superadmin") ||
    pathname.startsWith("/api") ||
    pathname.startsWith("/sse")
  );
}

function patchHistoryForAnalytics(): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as Window & Record<typeof HISTORY_PATCH_KEY, boolean>;
  if (w[HISTORY_PATCH_KEY]) return;
  w[HISTORY_PATCH_KEY] = true;
  for (const method of ["pushState", "replaceState"] as const) {
    const original = window.history[method];
    window.history[method] = function patchedHistoryMethod(...args) {
      const result = original.apply(this, args);
      window.dispatchEvent(new Event("hv:locationchange"));
      return result;
    };
  }
}

function usePublicPageviewTracking(isErrorPage: boolean): void {
  useEffect(() => {
    if (typeof window === "undefined" || isErrorPage) return;
    patchHistoryForAnalytics();

    const sessionId = getOrCreateAnalyticsSessionId();
    if (!sessionId) return;
    const visitorId = getOrCreateAnalyticsVisitorId();

    let lastTracked = "";
    let referrer = document.referrer;

    const sendPageview = (): void => {
      const pathname = window.location.pathname;
      if (!shouldTrackPublicPath(pathname)) return;
      const current = `${pathname}${window.location.search}`;
      if (current === lastTracked) return;
      lastTracked = current;

      const payload = JSON.stringify({
        pathname: current,
        referrer: referrer || undefined,
        session_id: sessionId,
        user_id: visitorId ?? undefined,
        user_agent: window.navigator.userAgent,
      });
      referrer = window.location.href;

      if (typeof window.navigator.sendBeacon === "function") {
        const blob = new Blob([payload], { type: "application/json" });
        if (window.navigator.sendBeacon("/api/analytics/pageview", blob)) {
          return;
        }
      }

      void fetch("/api/analytics/pageview", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: payload,
        credentials: "same-origin",
        keepalive: true,
      }).catch(() => undefined);
    };

    const onLocationChange = (): void => {
      window.setTimeout(sendPageview, 0);
    };

    sendPageview();
    window.addEventListener("popstate", onLocationChange);
    window.addEventListener("hashchange", onLocationChange);
    window.addEventListener("hv:locationchange", onLocationChange);
    return () => {
      window.removeEventListener("popstate", onLocationChange);
      window.removeEventListener("hashchange", onLocationChange);
      window.removeEventListener("hv:locationchange", onLocationChange);
    };
  }, [isErrorPage]);
}

export default function Wrapper({ children }: { children: ReactNode }) {
  const ctx = usePageContext() as unknown as {
    pageProps?: Record<string, unknown>;
    data?: { pageProps?: Record<string, unknown> } | Record<string, unknown>;
    dehydratedState?: unknown;
    locale?: string;
    urlPathname?: string;
    is404?: boolean;
    abortReason?: unknown;
    errorWhileRendering?: unknown;
  };
  // Vike's data() hook stores the return value at pageContext.data. The
  // codebase's data() returns { pageProps: {...} }. Prefer that nested
  // shape; Vike can also expose an empty pageProps object, which should
  // not mask the actual data payload during prerender.
  const dataPageProps = (ctx.data as { pageProps?: Record<string, unknown> } | undefined)
    ?.pageProps;
  const props =
    dataPageProps ??
    (hasProps(ctx.pageProps) ? ctx.pageProps : undefined) ??
    (hasProps(ctx.data) ? (ctx.data as Record<string, unknown>) : undefined) ??
    ctx.pageProps ??
    {};

  const [queryClient] = useState(getQueryClient);

  const locale = (props.locale as string | undefined) ?? ctx.locale ?? "en";
  const templateId = resolveSerializedTemplateId(props.templateId);
  const t = createT(locale, "dashboard");

  // Set dir="rtl" on <html> for Arabic. This is the single source of truth
  // for RTL layout — components rely on CSS logical properties and MUST NOT
  // use rtl:flex-row-reverse (which causes double-reversal).
  // On the server side, vike-react sets the initial dir via the document props
  // (see each page's +data.ts documentProps); this effect keeps it in sync
  // during client-side locale changes.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = locale;
    document.body.dataset.template = templateId;
  }, [locale, templateId]);

  if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log("[Wrapper] injecting keys:", Object.keys(props));
  }

  const child = isValidElement(children)
    ? cloneElement(children, props as Record<string, unknown>)
    : children;

  function handleSubscribe() {
    if (typeof window !== "undefined") {
      window.location.href = `/${locale}/subscribe`;
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={ctx.dehydratedState as never}>
        <AppShell
          locale={locale}
          t={t}
          urlPathname={ctx.urlPathname}
          templateId={templateId}
          isErrorPage={Boolean(ctx.is404 || ctx.abortReason || ctx.errorWhileRendering)}
          onSubscribe={handleSubscribe}
        >
          {child}
        </AppShell>
      </HydrationBoundary>
    </QueryClientProvider>
  );
}

// ---------------------------------------------------------------------------
// Inner shell — rendered inside QueryClientProvider so hooks that use
// useQuery (like useOfflineCachedOutbreak) have access to a query client.
// ---------------------------------------------------------------------------

function AppShell({
  locale,
  t,
  urlPathname,
  templateId,
  isErrorPage,
  onSubscribe,
  children,
}: {
  locale: string;
  t: ReturnType<typeof createT>;
  urlPathname?: string;
  templateId: TemplateId;
  isErrorPage: boolean;
  onSubscribe: () => void;
  children: ReactNode;
}) {
  const isNestedShell = useContext(AppShellContext);
  if (isNestedShell) {
    return <>{children}</>;
  }

  return (
    <AppShellContext.Provider value={true}>
      <AppShellContent
        locale={locale}
        t={t}
        urlPathname={urlPathname}
        templateId={templateId}
        isErrorPage={isErrorPage}
        onSubscribe={onSubscribe}
      >
        {children}
      </AppShellContent>
    </AppShellContext.Provider>
  );
}

function AppShellContent({
  locale,
  t,
  urlPathname,
  templateId,
  isErrorPage,
  onSubscribe,
  children,
}: {
  locale: string;
  t: ReturnType<typeof createT>;
  urlPathname?: string;
  templateId: TemplateId;
  isErrorPage: boolean;
  onSubscribe: () => void;
  children: ReactNode;
}) {
  const { isDegraded, cachedAt } = useOfflineCachedOutbreak();
  const hasMounted = useHasMounted();
  const publicT = createT(locale, "landing");
  const safeLocale = isLocaleCode(locale) ? locale : DEFAULT_LOCALE;
  const currentPathname =
    urlPathname ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const showPublicChrome = !isErrorPage && shouldShowPublicChrome(currentPathname);
  const showSuperadminChrome = !isErrorPage && shouldShowSuperadminChrome(currentPathname);
  const logicalPath = stripLocalePrefix(currentPathname);
  const isImmersiveHome =
    showPublicChrome && logicalPath === "/" && templateId === "bento-console";
  usePublicPageviewTracking(isErrorPage);

  const pwaStrings = {
    title: t("pwa.install.title"),
    subtitle: t("pwa.install.subtitle"),
    cta: t("pwa.install.cta"),
    dismiss: t("pwa.install.dismiss"),
    iosStep1: t("pwa.install.ios.step1"),
    iosStep2: t("pwa.install.ios.step2"),
    iosStep3: t("pwa.install.ios.step3"),
  };
  const updateStrings = {
    banner: t("pwa.update.banner"),
    reload: t("pwa.update.reload"),
    reloadLater: t("pwa.update.reloadLater"),
  };

  return (
    <>
      {showPublicChrome ? (
        <style
          // Critical guard for first paint and PWA startup before Vite CSS is applied.
          dangerouslySetInnerHTML={{ __html: PUBLIC_MOBILE_CRITICAL_CSS }}
        />
      ) : null}
      {showPublicChrome ? (
        <a href="#main-content" className="skip-link">
          {publicT("skipToContent")}
        </a>
      ) : null}
      {hasMounted ? (
        <OfflineBanner isDegraded={isDegraded} cachedAt={cachedAt} locale={locale} t={t} />
      ) : null}
      {isImmersiveHome ? (
        <>
          <LocaleReconciler serverLocale={safeLocale} currentLogicalPath="/" />
          {children}
        </>
      ) : showPublicChrome ? (
        <PublicChromeRouter locale={locale} t={publicT}>
          {children}
        </PublicChromeRouter>
      ) : showSuperadminChrome ? (
        <SuperadminChrome locale={locale} urlPathname={currentPathname}>
          {children}
        </SuperadminChrome>
      ) : (
        children
      )}
      {isImmersiveHome ? null : <StickyMobileCta t={t} onSubscribe={onSubscribe} />}
      <PWAInstallPrompt strings={pwaStrings} appName="Hantavirus" />
      <PWAUpdateBanner strings={updateStrings} />
    </>
  );
}
